import type React from 'react'
import { PORT_HIT_SIZE, type PortPoint } from '@/lib/text-linking/text-linking-geometry'

import { PLUS_SIZE } from '../constants/port-style'
import { portControlAttrs } from '../handlers/port-control-attrs'
import type { PortControlProps } from '../types/port-control'
import { PortBadgeContainer } from './port-badge-container'
import { PortFocusRing } from './port-focus-ring'

const PLUS_STROKE_COLOR = 'var(--color-page-editor-flow-text, #dc2626)'

/**
 * The unlinked out-port's "+" — an open port on the frame's border, where a resize handle would
 * otherwise be, drawn on the exact same shared badge tile every other in/out port square uses
 * (`PortBadgeContainer`), dashed while open — only
 * the content painted on top differs: a red plus instead of a chain number or nothing. It used to
 * render as a bare cross with no square at all, which read as a stray mark once it lost its
 * container (WMPCM-9223 follow-up); this square is that container.
 *
 * `center` is the already rotation-aware edge midpoint (see `outPortPoint`), in screen pixels; the
 * whole glyph — container AND cross alike — rotates with the frame too (WMPCM-9223), same as the
 * numbered squares' own border. Unlike a chain numeral, a plain cross has no reading orientation to
 * protect, so it is NOT counter-rotated back upright the way `PortSquare`'s numeral is.
 * `TextOverflowBadge`'s own "+" tile rotates the same way, for the same reason its doc comment
 * gives: the two are meant to coincide exactly at every angle, not just 0. No counter-scale of its
 * own to apply — this whole layer is screen space now, so every size below is already a literal
 * on-screen measurement.
 *
 * **Container suppression, WMPCM-9868.** `showContainer` is false only where `TextOverflowBadge`
 * is painting its own tile at this exact spot (an overflowing frame has no next frame, so its
 * out-port is always this glyph) — drawing this square underneath it would double up the chrome
 * the two are meant to coincide as. The cross itself is unaffected either way, so the port stays
 * clickable and keyboard-reachable regardless of which frame it's on.
 */
export function OutPortPlusGlyph({
  center,
  rotationDeg,
  showContainer,
  control,
}: {
  center: PortPoint
  /** Frame rotation — the container AND the cross both turn with it (see the doc comment above). */
  rotationDeg: number
  /** False only where `TextOverflowBadge` already occupies this spot — see the doc comment above. */
  showContainer: boolean
  control?: PortControlProps
}): React.JSX.Element {
  return (
    <g
      transform={`translate(${center.x}, ${center.y}) rotate(${rotationDeg})`}
      aria-hidden={control ? undefined : true}
    >
      <rect
        ref={control?.registerRef}
        x={-PORT_HIT_SIZE / 2}
        y={-PORT_HIT_SIZE / 2}
        width={PORT_HIT_SIZE}
        height={PORT_HIT_SIZE}
        fill="transparent"
        data-port-role="out"
        {...portControlAttrs(control)}
      />
      {showContainer && <PortBadgeContainer />}
      <path
        d={`M 0 ${-PLUS_SIZE / 2} V ${PLUS_SIZE / 2} M ${-PLUS_SIZE / 2} 0 H ${PLUS_SIZE / 2}`}
        stroke={PLUS_STROKE_COLOR}
        // 1.333, not 1.5: `SelectionOverflowIcon` draws its own plus at this width and sits at
        // exactly this coordinate, so any other value reads as two different glyphs (WMPCM-9199).
        strokeWidth={1.333}
        strokeLinecap="round"
        pointerEvents="none"
        aria-hidden="true"
      />
      <PortFocusRing visible={control?.focused ?? false} />
    </g>
  )
}
